// SignInPanel — five OAuth provider buttons + email/password fallback.
// Used standalone (anonymous landing modal) and embedded inside UpsellModal.

import { useState } from 'react';
import { ALL_PROVIDERS, providerLabel, signInWith, signInWithEmail, signUpWithEmail } from './signIn';
import type { OAuthProvider } from './types';

interface Props {
  /** Optional title shown above the buttons. */
  title?: string;
  /** Optional one-line context shown below the title. */
  subtitle?: string;
}

export default function SignInPanel({ title = 'Sign in', subtitle }: Props) {
  const [busy, setBusy] = useState<OAuthProvider | 'email' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'in' | 'up'>('in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  async function doOAuth(p: OAuthProvider) {
    try { setBusy(p); setError(null); await signInWith(p); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); setBusy(null); }
    // On success the browser redirects away; no need to clear busy.
  }

  async function doEmail(e: React.FormEvent) {
    e.preventDefault();
    try {
      setBusy('email'); setError(null);
      if (mode === 'in') await signInWithEmail(email, password);
      else                await signUpWithEmail(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setBusy(null); }
  }

  return (
    <div className="signin-panel">
      <h2 className="signin-title">{title}</h2>
      {subtitle && <p className="signin-subtitle">{subtitle}</p>}

      <div className="signin-providers">
        {ALL_PROVIDERS.map(p => (
          <button
            key={p}
            className={`signin-provider signin-provider-${p}`}
            disabled={!!busy}
            onClick={() => doOAuth(p)}
          >
            {busy === p ? 'Redirecting…' : providerLabel(p)}
          </button>
        ))}
      </div>

      <div className="signin-divider"><span>or</span></div>

      <form className="signin-email" onSubmit={doEmail}>
        <input
          type="email"
          placeholder="you@company.com"
          autoComplete="email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
        />
        <input
          type="password"
          placeholder="Password"
          autoComplete={mode === 'in' ? 'current-password' : 'new-password'}
          required
          minLength={8}
          value={password}
          onChange={e => setPassword(e.target.value)}
        />
        <button type="submit" className="signin-email-submit" disabled={!!busy}>
          {busy === 'email' ? '…' : mode === 'in' ? 'Sign In' : 'Create Account'}
        </button>
        <button
          type="button"
          className="signin-mode-toggle"
          onClick={() => setMode(m => m === 'in' ? 'up' : 'in')}
        >
          {mode === 'in' ? 'Need an account? Sign up' : 'Have an account? Sign in'}
        </button>
      </form>

      {error && <div className="signin-error" role="alert">{error}</div>}

      <p className="signin-trial">
        New accounts get a 7-day trial and 10 free tokens — no credit card needed.
      </p>
    </div>
  );
}
